import { Avatar, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Fab, IconButton, List, ListItem, ListItemAvatar, ListItemIcon, ListItemSecondaryAction, ListItemText, ListSubheader, Switch, TextField, Typography, useTheme } from '@material-ui/core';
import { Add, Business, Check, Delete, Link as LinkIcon } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import copy from 'clipboard-copy';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';

import { Collections } from 'Collections';
import { useAppUser } from 'components/AppUserProvider';
import { FullscreenLoading } from 'components/FullscreenLoading';
import { IndustryTypeSelect } from 'components/IndustryTypeSelect';
import { useOrgIdLocalStorage } from 'components/OrgProvider';
import { PageNavigation } from 'components/PageNavigation';
import { analytics, db } from 'FirebaseConfigs';
import { FirebaseUtils } from 'firebaseUtils/FirebaseUtils';
import { useEmailInviteExpirationFilter, useInviteExpirationFilter } from 'hooks/useInviteExpirationFilter';
import { acceptEmailInvite, rejectEmailInvite } from 'modelActions/OrgUserEmailInvite';
import { acceptInvite, rejectInvite } from 'modelActions/OrgUserInvite';
import { Address } from 'models/Address';
import { AnalyticsEvent } from 'models/AnalyticsEvent';
import { AppUser, noAppUserNameText } from 'models/AppUser';
import { noEmailText } from 'models/Email';
import { EnqueueSnacbar } from 'models/EnqueueSnacbar';
import { DbDoc, WithRef } from 'models/FbDoc';
import { IndustryTypes } from 'models/IndustryTypes';
import { Nullable } from 'models/Nullable';
import { noOrgNameText, Org } from 'models/Org';
import { OrgRoles } from 'models/OrgRoles';
import { OrgUser, orgUserIdFrom } from 'models/OrgUser';
import { OrgUserEmailInvite } from 'models/OrgUserEmailInvite';
import { OrgUserInvite } from 'models/OrgUserInvite';
import { PriceField } from 'models/PriceField';
import { PriceFieldTemplate } from 'models/PriceFieldTemplate';
import { EnumPresentMap, SearchableEnumPresent } from 'models/PropertyMap';
import { textValueFrom } from 'models/TextValue';
import { Paths } from 'Paths';
import { handleEnterKey } from 'Util/handleEnterKey';

import { AddressInput } from './MyOrgScreen';
import { WarningModal } from './Users/OrgUserScreen';

export function createOrgUser(inviteId: Nullable<string>, orgId: string, appUser: AppUser, roles: EnumPresentMap<OrgRoles.OrgRoles>, enqueueSnacbar: EnqueueSnacbar) {
    return new Promise<string>((resolve) => {
        FirebaseUtils.createDocument<OrgUser>(
            true,
            Collections.ORG_USERS,
            orgUserIdFrom(orgId, appUser.id),
            appUser,
            null,
            AnalyticsEvent.CREATED_ORG_USER,
            AnalyticsEvent.CREATED_ORG,
            AnalyticsEvent.CREATE_ORG_USER_ERROR,
            enqueueSnacbar,
            'Org Bruker opprettet',
            {
                userId: appUser.id,
                org: {
                    id: orgId,
                },
                orgContactInfo: appUser.contactInfo,
                personalContactInfo: appUser.contactInfo,
                avatarImage: appUser.avatarImage,
                orgRoles: {
                    map: roles,
                },
                invite: inviteId === null ? null : {
                    id: inviteId,
                }
            },
            (doc) => ({
                orgId: orgId,
                orgUserId: doc.id,
            }),
            orgUserId => {
                resolve(orgUserId)
            }
        )
    })
}

export function createOrg(appUser: AppUser, orgNr: Nullable<string>, address: Nullable<Address>, orgName: Nullable<string>, industryTypes: SearchableEnumPresent<IndustryTypes.IndustryTypes>, enqueueSnacbar: EnqueueSnacbar, isDemoOrg?: boolean) {
    return new Promise<string>((resolve) => {
        FirebaseUtils.createDocument<Org>(
            true,
            Collections.ORGS,
            null,
            appUser,
            null,
            AnalyticsEvent.CREATED_ORG,
            AnalyticsEvent.CREATED_ORG,
            AnalyticsEvent.CREATE_ORG_ERROR,
            enqueueSnacbar,
            'Org opprettet',
            {
                name: textValueFrom(orgName),
                owner: {
                    appUser,
                    orgUser: null,
                },
                logo: appUser.avatarImage,
                orgNr: textValueFrom(orgNr),
                address,
                offerTerms: null,
                industryTypes,
                hourRate: null,
                ...(isDemoOrg ? { isDemo: true } : {}),
            },
            (doc) => ({
                orgId: doc.id,
            }),
            async (newOrgId) => {
                await createOrgUser(null, newOrgId, appUser, {
                    [OrgRoles.FlatEnum.OWNER]: true,
                    [OrgRoles.FlatEnum.WORKER]: true,
                }, enqueueSnacbar)
                resolve(newOrgId)
            },
        )
    })
}

export function importAllIndustryTemplates(orgId: string, appUser: AppUser, industryTypeMap: EnumPresentMap<IndustryTypes.IndustryTypes>, enqueueSnackbar: EnqueueSnacbar) {
    IndustryTypes.FlatList.filter(it => !!industryTypeMap[it.id]).forEach(it => {
        db
            .collection(Collections.PRICE_FIELD_TEMPLATES)
            .where('data.template.priceField.industryTypes.map.' + it.id, '==', true)
            .get()
            .then(templates => {
                templates.forEach((doc) => {
                    const templatePriceFieldTemplate = doc.data() as DbDoc<PriceFieldTemplate>
                    FirebaseUtils.createDocument<PriceField>(
                        true,
                        Collections.PRICE_FIELDS,
                        null,
                        appUser || null,
                        null,
                        AnalyticsEvent.IMPORTED_PRICE_FIELD_TEMPLATE,
                        AnalyticsEvent.CREATED_ORG,
                        AnalyticsEvent.CREATE_PRICE_FIELD_ERROR,
                        enqueueSnackbar,
                        'Importerte prisfelter fra mal',
                        {
                            ...templatePriceFieldTemplate.data.template.priceField,
                            org: {
                                id: orgId,
                            },
                            template: {
                                id: doc.id,
                            },
                        },
                        (doc) => ({ priceFieldId: doc.id, templateId: doc.id }),
                    )
                })
            }).catch(console.error)
    })
}

type MyOrgUserInvitesProps = {
    invites?: WithRef<DbDoc<OrgUserInvite>>[] | null
    emailInvites?: WithRef<DbDoc<OrgUserEmailInvite>>[] | null
    appUser?: Nullable<AppUser>
}

export function MyOrgUserInvitesList({ invites, emailInvites, appUser }: MyOrgUserInvitesProps) {
    const { enqueueSnackbar } = useSnackbar()
    const history = useHistory()
    const [, setOrgId] = useOrgIdLocalStorage()
    return (
        <>
            {(invites?.length || emailInvites?.length) ? <ListSubheader>Invitasjoner</ListSubheader> : null}
            {!invites?.length ? null : (
                <>
                    {invites?.map(({ data: invite }) => (
                        <ListItem key={invite.id} button component={Link} to={Paths.APP_USERS + '/' + invite.inviterUser.appUser.id}>
                            <ListItemAvatar>
                                <Avatar src={invite?.org.logo?.original.url || undefined}>
                                    <Business />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={noOrgNameText(invite.org.name?.text)}
                                secondary={'Fra ' + noAppUserNameText(invite.inviterUser.orgUser.orgContactInfo?.name?.fullName)}
                            />
                            {appUser ? (
                                <ListItemSecondaryAction>
                                    <IconButton
                                        color="primary"
                                        aria-label="Bli med"
                                        onClick={() => {
                                            acceptInvite(appUser, invite, enqueueSnackbar)
                                            createOrgUser(invite.id, invite.org.id, appUser, invite.allottedRoles.map, enqueueSnackbar)
                                                .then(() => {
                                                    setOrgId(invite.org.id)
                                                    history.push(Paths.MY_ORG_USER)
                                                })
                                        }}><Check /></IconButton>
                                    <IconButton
                                        color="secondary"
                                        aria-label="Avvis"
                                        onClick={() => {
                                            rejectInvite(appUser, invite, enqueueSnackbar)
                                        }}><Delete /></IconButton>
                                </ListItemSecondaryAction>
                            ) : null}
                        </ListItem>
                    ))}
                </>
            )}
            {!emailInvites?.length ? null : (
                <>
                    {emailInvites?.map(({ data: invite }) => (
                        <ListItem key={invite.id} button component={Link} to={Paths.APP_USERS + '/' + invite.invite.inviterUser.appUser.id}>
                            <ListItemAvatar>
                                <Avatar src={invite?.invite.org.logo?.original.url || undefined}>
                                    <Business />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={noOrgNameText(invite.invite.org.name?.text)}
                                secondary={'Fra ' + noAppUserNameText(invite.invite.inviterUser.orgUser.orgContactInfo?.name?.fullName) + ' til ' + noEmailText(invite.invitedUser.appUserEmail.email)}
                            />
                            {appUser ? (
                                <ListItemSecondaryAction>
                                    <IconButton
                                        color="primary"
                                        aria-label="Bli med"
                                        onClick={() => {
                                            acceptEmailInvite(appUser, invite, enqueueSnackbar)
                                            createOrgUser(invite.id, invite.invite.org.id, appUser, invite.invite.allottedRoles.map, enqueueSnackbar)
                                                .then(() => {
                                                    setOrgId(invite.invite.org.id)
                                                    history.push(Paths.MY_ORG_USER)
                                                })
                                        }}><Check /></IconButton>
                                    <IconButton
                                        color="secondary"
                                        aria-label="Avvis"
                                        onClick={() => {
                                            rejectEmailInvite(appUser, invite, enqueueSnackbar)
                                        }}><Delete /></IconButton>
                                </ListItemSecondaryAction>
                            ) : null}
                        </ListItem>
                    ))}
                </>
            )}
        </>
    )
}

export function useMyOrgUserInvites(): [invites: WithRef<DbDoc<OrgUserInvite>>[] | null, loading: boolean] {
    const [appUser, loadingAppUser] = useAppUser()

    const [myInvites, loadingInvites] = FirebaseUtils.useCollectionData<OrgUserInvite>(
        true,
        Collections.ORG_USER_INVITES,
        AnalyticsEvent.GET_ORG_USER_INVITES_ERROR,
        {},
        (query, path) => (
            appUser && query.where(path.data.invitedUser.appUser.id, '==', appUser.data.id).where(path.data.active, '==', true)
        ))


    const [nonExpiredInvites] = useInviteExpirationFilter(myInvites)

    return [nonExpiredInvites, loadingInvites || loadingAppUser]
}


export function useEmailInviteExistingOrgUserFilter(invites: Nullable<WithRef<DbDoc<OrgUserEmailInvite>>[]>, orgUsers: Nullable<WithRef<DbDoc<OrgUser>>[]>): [invites: WithRef<DbDoc<OrgUserEmailInvite>>[] | null] {
    const newOrgInvites = useMemo(() => {
        //TODO: make sure this does not refresh recursively
        //console.log('compute', invites)
        const existingOrgIds = orgUsers?.map(({ data: orgUser }) => orgUser.org.id) || []
        return (
            invites?.filter(invite => !existingOrgIds.includes(invite.data.invite.org.id))
        )
    }, [invites, orgUsers])

    return [newOrgInvites || null]
}



export function useMyOrgUserEmailInvites(): [invites: WithRef<DbDoc<OrgUserEmailInvite>>[] | null, loading: boolean] {
    const [appUser, loadingAppUser] = useAppUser()
    const [myOrgUsers, loadingMyOrgUsers] = useMyOrgUsers()

    const [myInvites, loadingInvites] = FirebaseUtils.useCollectionData<OrgUserEmailInvite>(
        true,
        Collections.ORG_USER_EMAIL_INVITES,
        AnalyticsEvent.GET_ORG_USER_EMAIL_INVITES_ERROR,
        {},
        (query, path) => (
            (appUser && appUser.data.contactInfo?.email)
                ? query
                    .where(path.data.invitedUser.appUserEmail.searchStrings.email, '==', appUser.data.contactInfo.email.searchStrings.email)
                    .where(path.data.invite.active, '==', true)
                : null
        ))


    const [nonExpiredInvites] = useEmailInviteExpirationFilter(myInvites)
    const [newOrgInvites] = useEmailInviteExistingOrgUserFilter(nonExpiredInvites, myOrgUsers)

    return [newOrgInvites, loadingInvites || loadingAppUser || loadingMyOrgUsers]
}


type CreateOrgModalProps = {
    onClose: (orgId: string) => void
    title: string
    onCancel: () => void
    appUser: AppUser
}

function CreateOrgModal({ onClose, onCancel, title, appUser }: CreateOrgModalProps) {
    const { enqueueSnackbar } = useSnackbar()
    const [name, setName] = useState<string | null>('')
    const [orgNr, setOrgNr] = useState('')
    const [address, setAddress] = useState<Address | null>(null)
    //TODO: Industry type
    const [industryType, setIndustryType] = useState<IndustryTypes.IndustryType | null>(null)
    const [loading, setLoading] = useState(false)
    const [importAllTemplates, setImportAllTemplates] = useState(true)

    //TODO: Add validation
    const isValid = !!industryType

    async function onComplete() {
        if (isValid) {
            setLoading(true)
            const industryTypeMap = industryType ? { [industryType]: true } : {}
            const orgId = await createOrg(appUser, orgNr, address, name, { map: industryTypeMap }, enqueueSnackbar)
            if (importAllTemplates) {
                importAllIndustryTemplates(orgId, appUser, industryTypeMap, enqueueSnackbar)
            }
            onClose(orgId)
        }
    }

    const orgNrRef = useRef<any>();
    const addressRef = useRef<any>();
    const okButtonRef = useRef<any>();

    return (
        <Dialog
            open
            onClose={onCancel}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
            <DialogContent>
                <List>
                    <ListItem>
                        Hvis du er her for å kikke anbefaler vi å bruke elektro som demo bransje, fordi elektro har blitt satt opp med flest kategorier, rom og priser
                    </ListItem>
                    <ListItem>
                        <IndustryTypeSelect industryType={industryType} setIndustryType={setIndustryType} />
                    </ListItem>
                    <ListItem>
                        <ListItemText is="switch-list-label-use-templates" primary="Bruk standard maler" />
                        <ListItemSecondaryAction>
                            <Switch
                                color="secondary"
                                onChange={e => setImportAllTemplates(e.target.checked)}
                                checked={importAllTemplates}
                            />
                        </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                        <TextField
                            type="text"
                            variant="outlined"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="Navn"
                            autoFocus
                            fullWidth
                            onKeyPress={handleEnterKey(() => orgNrRef.current?.focus())}
                        />
                    </ListItem>
                    <ListItem>
                        <TextField
                            type="number"
                            variant="outlined"
                            value={orgNr}
                            onChange={e => setOrgNr(e.target.value)}
                            placeholder="OrgNr"
                            fullWidth
                            ref={orgNrRef}
                            onKeyPress={handleEnterKey(() => addressRef.current?.focus())}

                        />
                    </ListItem>
                    <ListItem>
                        <AddressInput
                            value={address}
                            onChange={(newAddress) => {
                                setAddress(newAddress)
                                okButtonRef.current?.focus()
                            }}
                        />
                    </ListItem>
                </List>
            </DialogContent>
            <DialogActions>
                <Button disabled={loading} onClick={onCancel}>Avbryt</Button>
                <Button disabled={!isValid || loading} onClick={() => onComplete().finally(() => setLoading(false))} ref={okButtonRef} color="primary" autoFocus>{'Ok'}</Button>
            </DialogActions>
        </Dialog>
    )
}

export function useMyOrgUsers(): [myOrgUsers: Nullable<WithRef<DbDoc<OrgUser>>[]>, loadingMyOrgUsers: boolean] {
    const [appUser] = useAppUser()

    const [myOrgUsers, loadingMyOrgUsers] = FirebaseUtils.useCollectionData<OrgUser>(
        true,
        Collections.ORG_USERS,
        AnalyticsEvent.GET_OWN_ORG_USERS_ERROR,
        {},
        (query, path) => (
            appUser && query.where(path.data.userId, '==', appUser.data.id)
        ))

    return [myOrgUsers || null, loadingMyOrgUsers]
}

export function SelectOrgScreen() {
    const { enqueueSnackbar } = useSnackbar()
    const theme = useTheme()
    const history = useHistory()
    const [appUserDoc, loadingAppUser] = useAppUser()
    const [, setOrgId] = useOrgIdLocalStorage()
    const appUser = appUserDoc?.data || null
    const [myOrgs, loadingMyOrgs] = FirebaseUtils.useCollectionData<Org>(
        true,
        Collections.ORGS,
        AnalyticsEvent.GET_ORGS_ERROR,
        {},
        (query, path) => (
            appUser && query.where(path.data.owner.appUser.id, '==', appUser.id)
        ))

    const [myOrgUsers, loadingMyOrgUsers] = useMyOrgUsers()

    const [orgs, setOrgs] = useState<Org[]>([])
    const [showCreateOrg, setShowCreateOrg] = useState(false)
    const [showInviteLink, setShowInviteLink] = useState(false)

    const myOrgIds = myOrgs?.map(org => org.data.id)
    const orgUsers = myOrgUsers?.filter(orgUser => !orgUser.__snap__.metadata.hasPendingWrites && !myOrgIds?.includes(orgUser.data.org.id))
    const orgIds = orgUsers?.map(ou => ou.data.org.id).sort()

    const fetchedOrgIds = Array.from(new Set(Object.values(orgs).map(org => org.id).sort()))
    const idsAreFetched = orgIds?.join(',') === fetchedOrgIds.join(',')

    const [myInvites, loadingInvites] = useMyOrgUserInvites()
    const [myEmailInvites, loadingEmailInvites] = useMyOrgUserEmailInvites()

    const [fetchStatus] = useState({ fetchingIds: null })

    useEffect(() => {
        //fetchStatus addresses race conditions as a result of rerenders
        const orgIdString = orgIds?.join(',')
        if (orgIds?.length === 0 && !idsAreFetched) {
            setOrgs([])
        }
        if ((!idsAreFetched && orgIds) && (fetchStatus.fetchingIds !== orgIdString) && (orgIds?.length > 0)) {
            //@ts-ignore
            fetchStatus.fetchingIds = orgIdString
            Promise.all(orgIds.map(orgId => (
                FirebaseUtils.getDocument<Org>(true, Collections.ORGS, orgId, appUser, null, AnalyticsEvent.GET_ORG_ERROR, enqueueSnackbar)
            ))).then(orgDocs => {
                if (orgIdString === fetchStatus.fetchingIds) {
                    setOrgs(orgDocs.map((orgDoc) => orgDoc?.data).filter((org): org is Org => !!org))
                    fetchStatus.fetchingIds = null
                }
            })

        }
    }, [idsAreFetched, orgIds, fetchStatus, enqueueSnackbar, appUser])

    const loading = loadingInvites || loadingEmailInvites || loadingMyOrgUsers || loadingMyOrgs || loadingAppUser
    if (loading) {
        return <FullscreenLoading screenId="SelectOrg" />
    }

    function onSelect(orgId: string, createdNew?: boolean) {
        setOrgId(orgId)
        history.push(createdNew ? Paths.MY_ORG : Paths.DASHBOARD)
    }

    function selectOrg(orgId: string, createdNew: boolean) {
        onSelect(orgId, createdNew)
        analytics.logEvent(AnalyticsEvent.SELECTED_ORG, {
            orgId,
            appUserId: appUser?.id,
        })
    }

    const appUserUrl = appUser ? (window.location.origin + Paths.APP_USERS + '/' + appUser.id) : null

    const shareData: Nullable<ShareData> = appUserUrl ? {
        title: "Forespørsel om invitasjon",
        text: 'Følg linken for å invitere meg til organisasjonen din:' + appUserUrl,
    } : null

    return (
        <PageNavigation title="Velg organisasjon">
            <Fab
                color="secondary"
                aria-label="add-offer"
                style={{ position: "fixed", bottom: 20, right: 20 }}
                onClick={() => setShowCreateOrg(true)}
            >
                <Add />
            </Fab>
            {(showCreateOrg && appUser) ? (
                <CreateOrgModal
                    title="Lag en ny organisasjon"
                    appUser={appUser}
                    onCancel={() => setShowCreateOrg(false)}
                    onClose={(newOrgId) => {
                        setShowCreateOrg(false)
                        selectOrg(newOrgId, true)
                    }}
                />
            ) : null}
            <List>
                {(showInviteLink && appUserUrl) ? (
                    <WarningModal
                        title="Be om invitasjon"
                        onClose={() => setShowInviteLink(false)}
                        onCancel={() => setShowInviteLink(false)}
                        noAutoFocus
                        noCancel
                    >
                        <Typography variant="subtitle2" gutterBottom>
                            Under finner du en link til AppBrukeren din.
                        </Typography>
                        <Typography variant="subtitle2" gutterBottom>
                            For at din arbeidsgiver skal kunne invitere deg til organisasjonen sin må de ha denne linken.
                        </Typography>
                        <Alert severity="warning" style={{ width: '100%' }}>
                            Ikke del denne linken med noen andre enn arbeidsgiver
                        </Alert>
                        <Box my={1} />
                        <TextField
                            value={appUserUrl}
                            fullWidth
                            variant="outlined"
                            InputProps={{
                                endAdornment: (
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        autoFocus
                                        style={{ marginLeft: theme.spacing(2) }}
                                        onClick={() => copy(appUserUrl).then(() => {
                                            enqueueSnackbar('Kopiert!', { variant: 'success' })
                                            setShowInviteLink(false)
                                        })
                                        }>
                                        Kopier
                                    </Button>
                                )
                            }}
                        />
                        {shareData && (navigator as any)?.canShare?.(shareData) ? (
                            <>
                                <Box mt={1} />
                                <Button
                                    color="primary"
                                    variant="contained"
                                    autoFocus
                                    fullWidth
                                    onClick={() => navigator.share(shareData)}
                                >
                                    Be arbeidsgiver om invitasjon
                                </Button>
                            </>
                        ) : null}
                    </WarningModal>
                ) : null}
                {appUser && (
                    <ListItem button onClick={() => setShowInviteLink(true)}>
                        <ListItemIcon>
                            <LinkIcon />
                        </ListItemIcon>
                        <ListItemText
                            primary="Bli med i en eksisterende organisasjon"
                            secondary="Trykk her og del brukeren din for å bli med i en organisasjon"
                        />
                    </ListItem>
                )}
                <MyOrgUserInvitesList invites={myInvites} emailInvites={myEmailInvites} appUser={appUser} />
                {!myOrgs?.length ? null : (
                    <>
                        <ListSubheader>Dine Organisasjoner</ListSubheader>
                        {myOrgs?.map(({ data: org }) => {
                            return (
                                <ListItem key={org.id} button onClick={() => {
                                    selectOrg(org.id, false)
                                }}>
                                    <ListItemAvatar>
                                        <Avatar src={org?.logo?.original.url || undefined}>
                                            <Business />
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText primary={noOrgNameText(org.name?.text)} />
                                </ListItem>
                            )
                        })}
                    </>
                )}
                {!orgs?.length ? null : (
                    <>
                        <ListSubheader>Organisasjoner</ListSubheader>
                        {orgs?.map(org => {
                            return (
                                <ListItem
                                    key={org.id}
                                    button
                                    onClick={() => {
                                        selectOrg(org.id, false)
                                    }}>
                                    <ListItemAvatar>
                                        <Avatar src={org?.logo?.original.url || undefined}>
                                            <Business />
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText primary={noOrgNameText(org?.name?.text)} />
                                </ListItem>
                            )
                        })}
                    </>
                )}
                {(myOrgUsers?.length || myOrgs?.length || myInvites?.length) ? null : (
                    <ListItem button onClick={() => setShowCreateOrg(true)}>
                        <ListItemText
                            primary="Du er ikke med i noen organisasjoner"
                            secondary="Vent på en invitasjon eller trykk her for å lage en ny organisasjon"
                        />
                    </ListItem>
                )}
            </List>
        </PageNavigation >
    )
}